import { useState } from 'react';
import { MapPin, Phone, Building2, StickyNote, Plus, Loader2, Clock, Pencil, CalendarClock } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useClientContext } from '../ClientContext';
import { useClientNotes, useCreateNote, useUploadImages } from '../hooks/useClientNotes';
import { useSetClientPreference } from '@/features/visits/hooks/useWeeklyVisits';
import { NotesList } from '../components/NotesList';
import { NoteDetailSheet } from '../components/NoteDetailSheet';
import { ImageCapture } from '../components/ImageCapture';
import { ImagePreview } from '../components/ImagePreview';
import type { ClientNote } from '@/api/client-notes';
import { InvoiceSummarySection } from '../components/InvoiceSummarySection';

const DAY_NAMES: { value: number; label: string }[] = [
  { value: 1, label: 'Пн' },
  { value: 2, label: 'Вт' },
  { value: 3, label: 'Ср' },
  { value: 4, label: 'Чт' },
  { value: 5, label: 'Пт' },
  { value: 6, label: 'Сб' },
  { value: 7, label: 'Вс' },
];

const DAY_SHORT: Record<number, string> = {
  1: 'пн', 2: 'вт', 3: 'ср', 4: 'чт', 5: 'пт', 6: 'сб', 7: 'вс',
};

export function InfoTab() {
  const { detail, detailLoading, clientId, officeId } = useClientContext();

  const { data: notes = [], isLoading: notesLoading } = useClientNotes(detail?.client?.id);
  const createNote = useCreateNote();
  const uploadImages = useUploadImages();
  const setPreference = useSetClientPreference();

  const [newNoteText, setNewNoteText] = useState('');
  const [selectedNote, setSelectedNote] = useState<ClientNote | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  // Preference editing
  const [prefDialogOpen, setPrefDialogOpen] = useState(false);
  const [editDay, setEditDay] = useState<number | null>(null);
  const [editTime, setEditTime] = useState('');
  const [editNote, setEditNote] = useState('');

  const isCreating = createNote.isPending || uploadImages.isPending;

  const handleAddFiles = (files: File[]) => {
    setPendingFiles((prev) => [...prev, ...files]);
  };

  const handleRemoveFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCreateNote = () => {
    const cid = detail?.client?.id;
    if (!cid || (!newNoteText.trim() && pendingFiles.length === 0)) return;

    const text = newNoteText.trim() || '(фото)';

    createNote.mutate(
      { clientId: cid, text },
      {
        onSuccess: (note) => {
          setNewNoteText('');
          if (pendingFiles.length > 0) {
            uploadImages.mutate(
              { clientId: cid, noteId: note.id, files: pendingFiles },
              {
                onSuccess: () => setPendingFiles([]),
                onError: () => setPendingFiles([]),
              },
            );
          }
        },
      },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCreateNote();
    }
  };

  const openPrefDialog = () => {
    const pref = detail?.preference;
    setEditDay(pref?.preferredDayOfWeek ?? null);
    setEditTime(pref?.preferredTime ?? '');
    setEditNote(pref?.note ?? '');
    setPrefDialogOpen(true);
  };

  const handleSavePreference = () => {
    setPreference.mutate(
      {
        clientId,
        preference: {
          officeId: officeId ?? null,
          preferredDayOfWeek: editDay,
          preferredTime: editTime || null,
          note: editNote || null,
        },
      },
      {
        onSuccess: () => setPrefDialogOpen(false),
      },
    );
  };

  if (detailLoading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  const client = detail?.client;
  const preference = detail?.preference;

  return (
    <div className="p-4 space-y-4">
      {/* Legal info */}
      {client?.legalName && (
        <div className="flex gap-3 p-3 rounded-lg bg-muted/50">
          <Building2 className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
          <div className="flex-1 space-y-1">
            <p className="text-sm text-muted-foreground">{client.legalName}</p>
            {client.inn && (
              <p className="text-xs text-muted-foreground">УНН: {client.inn}</p>
            )}
            {(client.address || client.city) && (
              <div className="flex items-start gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  {[client.city, client.address].filter(Boolean).join(', ')}
                </p>
              </div>
            )}
            {client.phone && (
              <div className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <a
                  href={`tel:${client.phone}`}
                  className="text-xs text-primary hover:underline"
                >
                  {client.phone}
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invoice summary */}
      {client && <InvoiceSummarySection clientId={client.id} />}

      {/* Visit preferences */}
      <div className="border-t pt-4 space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-medium flex items-center gap-2">
            <CalendarClock className="h-4 w-4" />
            Настройки визита
          </h3>
          <Button variant="ghost" size="sm" onClick={openPrefDialog}>
            <Pencil className="h-3.5 w-3.5 mr-1" />
            Изменить
          </Button>
        </div>

        <div className="p-3 rounded-lg bg-muted/50 space-y-1.5">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">День:</span>
            <span>{preference?.preferredDayOfWeek ? DAY_SHORT[preference.preferredDayOfWeek] : 'не задан'}</span>
          </div>
          {preference?.preferredTime && (
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <span>{preference.preferredTime}</span>
            </div>
          )}
          {preference?.note && (
            <p className="text-xs text-muted-foreground">{preference.note}</p>
          )}
        </div>
      </div>

      {/* Preference edit dialog */}
      <Dialog open={prefDialogOpen} onOpenChange={setPrefDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Настройки визита</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Day of week buttons */}
            <div className="space-y-2">
              <Label>Предпочтительный день</Label>
              <div className="flex gap-1.5 flex-wrap">
                {DAY_NAMES.map(({ value, label }) => (
                  <Button
                    key={value}
                    variant={editDay === value ? 'default' : 'outline'}
                    size="sm"
                    className="w-10"
                    onClick={() => {
                      const newDay = editDay === value ? null : value;
                      setEditDay(newDay);
                      if (!newDay) setEditTime('');
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Time */}
            <div className="space-y-2">
              <Label htmlFor="prefTime">Предпочтительное время</Label>
              <div className="flex gap-2">
                <Input
                  id="prefTime"
                  type="time"
                  className="flex-1 min-w-0"
                  value={editTime}
                  onChange={(e) => setEditTime(e.target.value)}
                  disabled={!editDay}
                />
                {editTime && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    onClick={() => setEditTime('')}
                  >
                    Сбросить
                  </Button>
                )}
              </div>
            </div>

            {/* Note */}
            <div className="space-y-2">
              <Label htmlFor="prefNote">Заметка</Label>
              <Input
                id="prefNote"
                placeholder="Заметка о визите..."
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPrefDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleSavePreference} disabled={setPreference.isPending}>
              {setPreference.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notes Section */}
      <div className="border-t pt-4 space-y-3">
        <h3 className="font-medium flex items-center gap-2">
          <StickyNote className="h-4 w-4" />
          Заметки
        </h3>

        {/* Quick create input */}
        <div className="flex gap-2">
          <Input
            placeholder="Добавить заметку..."
            value={newNoteText}
            onChange={(e) => setNewNoteText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isCreating}
          />
          <ImageCapture onCapture={handleAddFiles} disabled={isCreating} />
          <Button
            size="icon"
            onClick={handleCreateNote}
            disabled={(!newNoteText.trim() && pendingFiles.length === 0) || isCreating}
          >
            {isCreating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Image preview before sending */}
        <ImagePreview files={pendingFiles} onRemove={handleRemoveFile} />

        {/* Notes list */}
        {notesLoading ? (
          <Skeleton className="h-20" />
        ) : (
          <NotesList clientId={detail?.client?.id!} notes={notes} onSelect={setSelectedNote} />
        )}
      </div>

      {/* Note detail sheet */}
      <NoteDetailSheet
        note={selectedNote}
        open={!!selectedNote}
        onOpenChange={(open) => !open && setSelectedNote(null)}
      />
    </div>
  );
}
